import { useState } from "react";
import { cn } from "@/lib/utils";
import { ChevronDown, ChevronUp } from "lucide-react";

interface LetterCardProps {
  city: string;
  country: string;
  excerpt: string;
  fullText?: string;
  className?: string;
}

const LetterCard = ({ city, country, excerpt, fullText, className }: LetterCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasFullText = Boolean(fullText);

  return (
    <article
      className={cn(
        "letter-card group cursor-default",
        className
      )}
    >
      {/* Decorative stamp */}
      <div className="absolute right-3 md:right-6 top-3 md:top-6 h-10 w-10 md:h-16 md:w-16 rounded border-2 border-dashed border-tan/60 flex items-center justify-center opacity-60 transition-opacity">
        <span className="font-serif text-[8px] md:text-xs text-charcoal-light uppercase tracking-wider md:tracking-widest text-center leading-tight">
          {country}
        </span>
      </div>

      {/* City header */}
      <header className="mb-3 md:mb-6 pr-12 md:pr-20">
        <p className="text-xs uppercase tracking-[0.15em] md:tracking-[0.2em] text-muted-foreground mb-1 font-sans">
          A letter from
        </p>
        <h3 className="font-serif text-xl md:text-3xl text-primary font-semibold">
          {city}
        </h3>
      </header>

      {/* Letter content with smooth expand/collapse */}
      <blockquote className="letter-text relative pl-4 border-l-2 border-tan/50">
        <div
          className={cn(
            "overflow-hidden transition-all duration-300 ease-out",
            isExpanded ? "max-h-[2000px]" : "max-h-[200px]"
          )}
        >
          <p className={cn(
            "italic transition-opacity duration-300",
            isExpanded ? "opacity-0 hidden" : "opacity-100"
          )}>
            {excerpt}
          </p>
          <p className={cn(
            "italic transition-opacity duration-300",
            isExpanded ? "opacity-100" : "opacity-0 hidden"
          )}>
            {fullText || excerpt}
          </p>
        </div>
      </blockquote>

      {/* Expand/Collapse link - larger touch target for mobile */}
      {hasFullText && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 font-sans transition-colors duration-200 py-2 px-1 -ml-1 min-h-[44px] touch-manipulation"
        >
          {isExpanded ? (
            <>
              Show less
              <ChevronUp className="h-4 w-4" />
            </>
          ) : (
            <>
              Read full sample
              <ChevronDown className="h-4 w-4" />
            </>
          )}
        </button>
      )}

      {/* Signature flourish */}
      <footer className="mt-4 md:mt-6 flex items-center gap-3">
        <div className="h-px flex-1 bg-gradient-to-r from-tan/50 to-transparent" />
        <span className="font-serif text-sm text-muted-foreground italic">
          — Eunice
        </span>
      </footer>
    </article>
  );
};

export default LetterCard;
